import type { Masjid, SalahBlankKey, SalahMode } from './types';
import type { DayTimes } from './prayer';
import { addDays, normaliseTime, ymd, zonedToInstant } from './time';

/** Delay choices after the jama'ah time, in seconds (15 s to 20 minutes). */
export const SALAH_DELAYS = [15, 30, 60, 90, 120, 150, 180, 240, 300, 360, 420, 480, 600, 720, 900, 1200] as const;
/** How long the screen stays blank, in minutes. */
export const SALAH_DURATIONS = [3, 5, 7, 8, 10, 12, 15, 20, 25, 30, 40, 45, 60] as const;
export const SALAH_KEYS: SalahBlankKey[] = ['fajr', 'dhuhr', 'asr', 'maghrib', 'isha', 'jumuah', 'eid'];
export const SALAH_KEY_LABEL: Record<SalahBlankKey, string> = { fajr: 'Fajr', dhuhr: 'Dhuhr', asr: 'Asr', maghrib: 'Maghrib', isha: 'Isha', jumuah: 'Jumu‘ah', eid: 'Eid' };

export const delayLabel = (sec: number) => (sec < 60 ? `${sec} seconds` : sec % 60 ? `${(sec / 60).toFixed(1).replace(/\.0$/, '')} minutes` : `${sec / 60} minute${sec === 60 ? '' : 's'}`);

/** Sensible starting values: Maghrib waits for the adhan and iqamah; Jumu'ah and Eid stay blank longer for the khutbah. */
export function defaultSalahMode(): SalahMode {
  const r = (delaySec: number, minutes: number) => ({ on: true, delaySec, minutes });
  return {
    enabled: false, clock: 'top-right', h12: false,
    prayers: { fajr: r(60, 10), dhuhr: r(120, 10), asr: r(60, 10), maghrib: r(240, 8), isha: r(60, 12), jumuah: r(60, 15), eid: r(60, 30) },
  };
}

export interface SalahBlank { key: SalahBlankKey; from: Date; until: Date }

/**
 * Whether screens should be blank now, and until when. Checks today's and yesterday's jama'ah times (so a late Isha
 * blank carries past midnight). On Fridays, Jumu'ah takes Dhuhr's place.
 */
export function salahBlankAt(masjid: Masjid, getDay: (day: string) => DayTimes, now: Date): SalahBlank | null {
  const sm = masjid.prayer.salahMode;
  if (!sm?.enabled) return null;
  const tz = masjid.timezone;
  const today = ymd(now, tz);
  let best: SalahBlank | null = null;
  for (const day of [addDays(today, -1), today]) {
    const dt = getDay(day);
    const moments: Array<[SalahBlankKey, string | undefined]> = [];
    for (const k of ['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'] as const) {
      if (k === 'dhuhr' && dt.isFriday && dt.jumuah.length) dt.jumuah.forEach((s) => moments.push(['jumuah', s.jamaah]));
      else moments.push([k, dt.jamaah[k]]);
    }
    for (const j of dt.eid?.jamaats ?? []) moments.push(['eid', j]);
    for (const [key, time] of moments) {
      const rule = sm.prayers[key];
      const t = normaliseTime(time ?? '');
      if (!rule?.on || !t) continue;
      const from = new Date(zonedToInstant(day, t, tz).getTime() + rule.delaySec * 1000);
      const until = new Date(from.getTime() + rule.minutes * 60_000);
      if (now >= from && now < until && (!best || until > best.until)) best = { key, from, until };
    }
  }
  return best;
}

/** The next time screens will blank (for the dashboard), within the coming day. */
export function nextSalahBlank(masjid: Masjid, getDay: (day: string) => DayTimes, now: Date): SalahBlank | null {
  const sm = masjid.prayer.salahMode;
  if (!sm?.enabled) return null;
  const tz = masjid.timezone;
  const today = ymd(now, tz);
  let best: SalahBlank | null = null;
  for (const day of [today, addDays(today, 1)]) {
    const dt = getDay(day);
    const moments: Array<[SalahBlankKey, string | undefined]> = [];
    for (const k of ['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'] as const) {
      if (k === 'dhuhr' && dt.isFriday && dt.jumuah.length) dt.jumuah.forEach((s) => moments.push(['jumuah', s.jamaah]));
      else moments.push([k, dt.jamaah[k]]);
    }
    for (const j of dt.eid?.jamaats ?? []) moments.push(['eid', j]);
    for (const [key, time] of moments) {
      const rule = sm.prayers[key];
      const t = normaliseTime(time ?? '');
      if (!rule?.on || !t) continue;
      const from = new Date(zonedToInstant(day, t, tz).getTime() + rule.delaySec * 1000);
      if (from > now && (!best || from < best.from)) best = { key, from, until: new Date(from.getTime() + rule.minutes * 60_000) };
    }
  }
  return best;
}
